/** @file puppetSync: keeps the viewport's puppet instance in step with the preview bundle and the playhead. */
import type { Scene } from '@babylonjs/core/scene.js';
import type { Clip } from '@zilo/pose3d-format/types/clip';
import type { LoadedBundle } from '@zilo/pose3d-runtime-babylon/bundle/loadedBundle';
import type { CameraSource } from '@zilo/pose3d-runtime-babylon/instance/puppetInstanceTypes';
import type { EditorState, PoseOverride } from '../../state/editorTypes';
import { frameSeek } from './frameSeek';
import { previewClip } from './previewClip';
import { previewPuppet, type PreviewPuppet } from './previewPuppet';
import { restClip } from './restClip';

/** Called while playing when the drawn frame changes (the playhead follows the runtime clock). */
export type FrameListener = (frame: number) => void;

/** `update` once per rendered frame; `current` is the instance being drawn (null until the first bundle loads). */
export type PuppetSync = {
  update(s: EditorState, dt: number, camera: CameraSource, hidden: readonly string[]): void;
  current(): PreviewPuppet | null;
  dispose(): void;
};

/** The sync's mutable state: the drawn puppet, the bundle wanted, the last seek, play state, the drawn-clip memo. */
type SyncState = {
  scene: Scene; puppet: PreviewPuppet | null; wanted: LoadedBundle | null; seek: { clip: string; frame: number; drawn: Clip } | null;
  playing: boolean; lastFrame: number; memo: { stored: Clip; override: PoseOverride | null; hidden: readonly string[]; rest: boolean; drawn: Clip } | null;
};

/** Builds the instance of `bundle`; it replaces the drawn one only if it is still wanted when ready. */
function load(st: SyncState, bundle: LoadedBundle | null): void {
  st.wanted = bundle;
  if (bundle === null) { st.puppet?.dispose(); st.puppet = null; return; }
  void previewPuppet(st.scene, bundle).then((next) => {
    if (st.wanted !== bundle || st.scene.isDisposed) { next.dispose(); return; }
    st.puppet?.dispose();
    Object.assign(st, { puppet: next, seek: null, memo: null, playing: false });
  });
}

/**
 * The clip to draw for `name` (memoised on its inputs) installed in the instance's clip record: with `camera.quads` on,
 * the rest pose (`restClip`, no override; isolate hiding kept), else the stored clip under the pose override.
 */
function drawnClip(st: SyncState, p: PreviewPuppet, name: string, s: EditorState, hidden: readonly string[]): Clip | undefined {
  const stored = p.source.clips[name];
  if (stored === undefined) return undefined;
  const rest = s.camera.quads;
  if (st.memo?.stored !== stored || st.memo.override !== s.poseOverride || st.memo.hidden !== hidden || st.memo.rest !== rest) {
    const drawn = rest ? previewClip(restClip(stored), null, hidden) : previewClip(stored, s.poseOverride, hidden);
    st.memo = { stored, override: s.poseOverride, hidden, rest, drawn };
  }
  if (p.clip(name) !== st.memo.drawn) p.setClip(name, st.memo.drawn);
  return st.memo.drawn;
}

/** Starts the clock running from the paused pose (or from 0 on another clip). */
function startPlaying(st: SyncState, p: PreviewPuppet, name: string): void {
  p.instance.play(name, { restart: st.seek?.clip !== name, blend: 0 });
  p.instance.setPaused(false);
  Object.assign(st, { playing: true, seek: null });
}

/** Seeks the paused instance to `frame` of `name` when clip, frame or the drawn clip changed since the last seek. */
function seekTo(st: SyncState, p: PreviewPuppet, name: string, frame: number): void {
  if (st.playing) { p.instance.setPaused(true); st.playing = false; }
  const drawn = st.memo?.drawn;
  if (drawn === undefined || (st.seek?.clip === name && st.seek.frame === frame && st.seek.drawn === drawn)) return;
  frameSeek(p.instance, name, frame);
  st.seek = { clip: name, frame, drawn };
}

/** Plays or seeks; returns the dt to update with (0 while paused, dragging or in Quads mode, which holds frame 0 of the rest clip). */
function clockStep(st: SyncState, p: PreviewPuppet, s: EditorState, dt: number, name: string): number {
  if (s.camera.quads) { seekTo(st, p, name, 0); return 0; }
  if (s.playhead.playing && s.poseOverride === null) {
    if (!st.playing) startPlaying(st, p, name);
    return dt * s.playhead.speed;
  }
  seekTo(st, p, name, s.poseOverride?.clip === name ? s.poseOverride.frame : s.playhead.frame);
  return 0;
}

/**
 * A new `preview.bundle` builds a new instance (the old one is drawn until the new one is ready, then disposed);
 * the drawn clip is `previewClip(stored, poseOverride, hidden)` (Quads mode: the rest pose, the playhead untouched); paused, the instance is `frameSeek`ed to the
 * override's frame (else the playhead's) whenever clip, frame or drawn clip changes, then updated with dt 0;
 * playing, it runs at `dt x speed` and reports its frame through `onFrame`.
 */
export function puppetSync(scene: Scene, onFrame: FrameListener): PuppetSync {
  const st: SyncState = { scene, puppet: null, wanted: null, seek: null, playing: false, lastFrame: -1, memo: null };
  return {
    update: (s, dt, camera, hidden) => {
      if (s.preview.bundle !== st.wanted) load(st, s.preview.bundle);
      const p = st.puppet;
      if (p === null) return;
      p.instance.setFacingSteps(s.camera.facingSteps);
      const name = s.playhead.clip;
      const ok = name !== null && drawnClip(st, p, name, s, hidden) !== undefined;
      p.instance.update(ok ? clockStep(st, p, s, dt, name) : 0, camera);
      const frame = p.instance.state().frame;
      if (st.playing && frame !== st.lastFrame) onFrame(frame);
      st.lastFrame = frame;
    },
    current: () => st.puppet,
    dispose: () => { st.wanted = null; st.puppet?.dispose(); st.puppet = null; },
  };
}
